import React from 'react';
import PropTypes from 'prop-types';
import { Typography } from '@mui/material';
import AppBox from '../../Layout/Box/AppBox';
import { useTheme } from '@mui/material/styles';
import { layoutSpacing } from '../../Tokens';

const SIZE_MAP = {
  sm: { fontSize: 13, defaultWeight: 500 },
  md: { fontSize: 15, defaultWeight: 600 },
};

/**
 * AppTitle — section/card title with an optional left accent bar and icon.
 *
 * @param {React.ComponentType} Icon       Icon component from Design_System/Assets/Icons
 * @param {'sm'|'md'}           size       'md' (default, 15px/600) or 'sm' (13px/500)
 * @param {boolean}             bold       Override font weight to semibold when true
 * @param {boolean}             showAccent Show the left accent bar (default true)
 * @param {object}              sx         Extra sx on the root AppBox
 */
const AppTitle = ({
  children,
  Icon,
  size = 'md',
  bold,
  showAccent = true,
  sx,
  ...props
}) => {
  const theme = useTheme();
  const { fontSize, defaultWeight } = SIZE_MAP[size] ?? SIZE_MAP.md;
  const fontWeight = bold ? 600 : defaultWeight;
  const accentHeight = size === 'sm' ? 14 : 16;

  return (
    <AppBox
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        position: 'relative',
        pl: showAccent ? '15px' : 0,
        ...(showAccent && {
          '&:before': {
            content: '""',
            position: 'absolute',
            left: 0,
            top: '50%',
            transform: 'translateY(-50%)',
            width: 4,
            height: accentHeight,
            backgroundColor: theme.palette.primary.main,
            borderRadius: 1,
          },
        }),
        ...sx,
      }}
      {...props}
    >
      {Icon && (
        <Icon
          sx={{
            fontSize: size === 'sm' ? 16 : 18,
            color: theme.palette.text.secondary,
            flexShrink: 0,
          }}
        />
      )}
      <Typography
        component="span"
        sx={{
          fontSize,
          fontWeight,
          color: theme.palette.text.primary,
          lineHeight: 1.4,
          letterSpacing: '0.3px',
        }}
      >
        {children}
      </Typography>
    </AppBox>
  );
};

AppTitle.propTypes = {
  children: PropTypes.node,
  Icon: PropTypes.elementType,
  size: PropTypes.oneOf(['sm', 'md']),
  bold: PropTypes.bool,
  showAccent: PropTypes.bool,
  sx: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
};

export default AppTitle;
